import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import axios from 'axios';

const AdComponent = ({ placement, className = "", onAdLoad = null, onAdClick = null }) => {
  const [adData, setAdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impressionId, setImpressionId] = useState(null);

  useEffect(() => {
    requestAd();
  }, [placement]);

  const requestAd = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return; // No ad for unauthenticated users
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ads/request`,
        { placement },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = response.data;
      
      if (data.should_show_ad && data.ad_content) {
        setAdData(data.ad_content);
        setImpressionId(data.impression_id);
        
        if (onAdLoad) {
          onAdLoad(data);
        }
      }
    } catch (error) {
      console.error('Error requesting ad:', error);
      setError('Failed to load ad');
    } finally {
      setLoading(false);
    }
  };

  const handleAdClick = async () => {
    try {
      if (impressionId) {
        const token = localStorage.getItem('token');
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/ads/click`,
          { impression_id: impressionId },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      if (onAdClick) {
        onAdClick(adData);
      }

      // Open destination URL
      if (adData?.destination_url) {
        window.open(adData.destination_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error tracking ad click:', error);
    }
  };

  // Don't render anything if loading, error, or no ad
  if (loading || error || !adData) {
    return null;
  }

  // Render different ad formats
  const renderBannerAd = () => (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Sponsored</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setAdData(null);
          }}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
      
      <div className="flex items-center space-x-4" onClick={handleAdClick}>
        {adData.image_url && (
          <img
            src={adData.image_url}
            alt={adData.title}
            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
          />
        )}
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm mb-1 truncate">
            {adData.title}
          </h3>
          <p className="text-gray-600 text-xs leading-relaxed mb-2">
            {adData.description}
          </p>
          <div className="flex items-center text-blue-600 text-xs font-medium">
            <span>{adData.cta}</span>
            <ExternalLink size={12} className="ml-1" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderNativeAd = () => (
    <div className={`bg-white border border-gray-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all duration-300 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Sponsored Content</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setAdData(null);
          }}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      
      <div onClick={handleAdClick}>
        {adData.image_url && (
          <img
            src={adData.image_url}
            alt={adData.title}
            className="w-full h-48 rounded-lg object-cover mb-4"
          />
        )}
        
        <h3 className="font-bold text-gray-900 text-lg mb-3">
          {adData.title}
        </h3>
        
        <p className="text-gray-600 leading-relaxed mb-4">
          {adData.description}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center text-blue-600 font-semibold">
            <span>{adData.cta}</span>
            <ExternalLink size={16} className="ml-2" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderInterstitialAd = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
        <div className="relative">
          <button
            onClick={() => setAdData(null)}
            className="absolute top-4 right-4 bg-black/20 hover:bg-black/40 text-white rounded-full p-2 transition-colors z-10"
          >
            <X size={20} />
          </button>
          
          {adData.image_url && (
            <img
              src={adData.image_url}
              alt={adData.title}
              className="w-full h-64 object-cover"
            />
          )}
        </div>
        
        <div className="p-6">
          <div className="text-center mb-4">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Sponsored</span>
          </div>
          
          <h3 className="font-bold text-gray-900 text-xl text-center mb-4">
            {adData.title}
          </h3>
          
          <p className="text-gray-600 text-center leading-relaxed mb-6">
            {adData.description}
          </p>
          
          <button
            onClick={handleAdClick}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
          >
            <span>{adData.cta}</span>
            <ExternalLink size={16} className="ml-2" />
          </button>
        </div>
      </div>
    </div>
  );

  // Render based on ad format
  switch (adData.ad_format) {
    case 'banner':
      return renderBannerAd();
    case 'native':
      return renderNativeAd();
    case 'interstitial':
      return renderInterstitialAd();
    default:
      return renderNativeAd();
  }
};

export default AdComponent;